import {Feature, Features} from "../feature";
import type {GraphAtom} from "../graph/GraphAtom"
import {MolecularGraph} from "../graph/MolecularGraph"
import {getElementName} from "../graph/elementNames"
import {generateId} from "../graph/idGenerator"
import {getMolecularFormula} from "../graph/molecularFormula"
import {hydrogenMask} from "../mask/hydrogenMask";
import {featureSorter} from "./featureSorter";
import {FeatureDisplayNames} from "../FeatureDisplayNames";
import {classifyFeature} from "../serialization/classifyFeature";

/**
 * PIPH-119: Compute IUPAC-style bridge numbering for a bridged bicyclic feature.
 * Returns a string like "2.2.1" or undefined if the feature doesn't have a valid
 * bridged bicyclic structure.
 */
function computeBridgeNumbering(feature: Feature, graph: MolecularGraph): string | undefined {
    // Find bridgeheads: atoms with ≥3 internal bonds (bonds to other atoms in feature)
    const featureAtomSet = new Set(feature.atoms);
    const bridgeheads: GraphAtom[] = [];
    for (const atom of feature.atoms) {
        const internalBondCount = graph
            .getBondsAttachedTo(atom)
            .filter(b => featureAtomSet.has(b.getOtherAtom(atom)))
            .length;
        if (internalBondCount >= 3) {
            bridgeheads.push(atom);
        }
    }
    if (bridgeheads.length !== 2) return undefined;

    // Build adjacency map restricted to feature atoms
    const adjByAtom = new Map<GraphAtom, GraphAtom[]>();
    for (const atom of feature.atoms) {
        const neighbors: GraphAtom[] = [];
        for (const bond of graph.getBondsAttachedTo(atom)) {
            const other = bond.getOtherAtom(atom);
            if (featureAtomSet.has(other)) {
                neighbors.push(other);
            }
        }
        adjByAtom.set(atom, neighbors);
    }

    // Find all simple paths between bridgeheads
    const [start, end] = bridgeheads;
    const maxDepth = feature.atoms.length;
    const allPaths: GraphAtom[][] = [];
    const visited = new Set<GraphAtom>([start]);
    const current: GraphAtom[] = [start];

    function dfs(node: GraphAtom) {
        if (node === end && current.length > 1) {
            allPaths.push(current.slice());
            return;
        }
        if (current.length > maxDepth) return;
        const neighbors = adjByAtom.get(node) ?? [];
        for (const next of neighbors) {
            if (visited.has(next)) continue;
            visited.add(next);
            current.push(next);
            dfs(next);
            current.pop();
            visited.delete(next);
        }
    }
    dfs(start);

    // A valid bridged bicyclic has at least 3 simple paths
    if (allPaths.length < 3) return undefined;

    // Sort by length (longest first)
    allPaths.sort((a, b) => b.length - a.length);

    // The two longest paths are main ring paths; shortest is the bridge
    // Path length minus 2 gives intermediate atom count (excluding bridgeheads)
    const pathLengths = [
        allPaths[0].length - 2,
        allPaths[1].length - 2,
        allPaths[allPaths.length - 1].length - 2,
    ];
    pathLengths.sort((a, b) => b - a);
    return pathLengths.join(".");
}

/**
 * Convert a 1-based index to an Excel-style column letter suffix.
 *   1 → "A", 2 → "B", ..., 26 → "Z", 27 → "AA", 28 → "AB", ...
 *
 * Used by PIPH-57 to label duplicate top-level features with letters instead
 * of numbers. The overflow scheme beyond Z matters only for extreme cases —
 * a molecule with >26 of the same feature type — but we handle it correctly
 * rather than silently overflowing.
 */
function toLetterSuffix(n: number): string {
    let result = "";
    let remaining = n;
    while (remaining > 0) {
        const rem = (remaining - 1) % 26;
        result = String.fromCharCode(65 + rem) + result;
        remaining = Math.floor((remaining - 1) / 26);
    }
    return result;
}

export const featureIdentifier = {
    getOrganicIdentifierName: (symbol: string) => {
        // Some element symbols mean something else in Organic Chemistry. For those elements,
        // We don't identify them by their element name, but just as the symbol.
        const organicOverrides = ["Pr", "R", "Ac", "Cn", "Ts"];
        if(!organicOverrides.includes(symbol)) {
            // Otherwise, try to find the element
            const name = getElementName(symbol);
            if(name) return name;
        }

        return symbol;
    },
    applyIdentifiers: (features: Feature[], graph: MolecularGraph) => {
        featureIdentifier.applyFeatureIds(features);
        featureIdentifier.applyFeatureUniqueNames(features, graph);
        // don't need to id atoms; this is already done in svg container
        // featureIdentifier.applyAtomNamesBasedOnFeature(features, graph);
    },
    getAtomNamesBasedOnFeature(features: Feature[], graph: MolecularGraph) {
        // first map keys are atom ids, second map keys are feature ids
        const atomNames: Record<string, Record<string, {displayName: string, ariaLabel: string}>> = {};

        // find atoms that are shared in top level features
        const topLevelFeatures = featureSorter.sortTopLevelFeatures(features);
        const repeatAtoms = topLevelFeatures.reduce((acc, feature) => {
            topLevelFeatures.forEach(f => {
                if(f !== feature) {
                    f.atoms.forEach(atom => {
                        if(feature.atoms.includes(atom)) {
                            acc.add(atom)
                        }
                    })
                }
            })
            return acc;
        }, new Set<GraphAtom>)
        //

        features.forEach((feature, i) => {
            const featureId = feature.id || i.toString();

            const impliedHydrogens = hydrogenMask.getFeatureImpliedHydrogens(feature, graph, false);

            let atoms: GraphAtom[];
            let atomIdNames : string[];

            // set atom names
            if(feature.name.includes(Features.ring)) {
                // name atoms based on ring index - NOT element count
                const ringIdNames = featureIdentifier.applyRingIndexedNames(feature.atoms);
                const impliedHsNames = featureIdentifier.applyIndexedNames(new Array(impliedHydrogens.length).fill("Hydrogen"));

                atoms = [...feature.atoms, ...impliedHydrogens];
                atomIdNames = [...ringIdNames, ...impliedHsNames];
            }
            else { // for all other features, name atoms based on element count
                // add H's connected to atoms in feature if not in feature already
                atoms = feature.atoms.concat(...impliedHydrogens);

                atomIdNames = featureIdentifier.applyIndexedNames(atoms.map(a =>
                    featureIdentifier.getOrganicIdentifierName(a.symbol)));
            }

            atomIdNames.forEach((shortName, i2) => {
                // if atom has already been labeled from a parent ring or chain
                // AND if this current parent is a ring or chain
                // this means this atom is double counted in ring/chain features
                // SO concatenate the names
                const atomId = atoms[i2].id || feature.id + i2.toString();
                const featureRecord = atomNames[atomId] || {};

                // use feature specific short name for aria label
                let atomFullName = shortName + " of " + (feature.uniqueName || feature.name);
                if(repeatAtoms.has(atoms[i2])) atomFullName += " shared";

                // now we change the atom short name to the one from parent if this is a child feature
                if(feature.parent && featureRecord[feature.parent.id!]) {
                    shortName = featureRecord[feature.parent.id!].displayName;
                }

                featureRecord[featureId] = {displayName: shortName, ariaLabel: atomFullName};
                atomNames[atomId] = featureRecord;
            })

        })

        return atomNames;
    },
    applyFeatureIds: (features: Feature[]) => {
        features.forEach(feature => feature.id = generateId())
    },
    applyFeatureUniqueNames: (features: Feature[], graph: MolecularGraph) => {
        const topLevelNames = features.map(f => {
            if(f.name.includes(Features.atom)) {
                // PIPH-122: Handle protonated atom features (e.g., atom.O.protonated → H₃O⁺)
                const isProtonated = f.name.includes(Features.protonated);
                const symbol = f.name.split(".")[1];
                // if lone atom is bonded to H's, it is not a true lone atom so the
                // feature name should be molecular formula
                const hydrogens = hydrogenMask.getFeatureImpliedHydrogens(f, graph, false);
                if(f.atoms.length > 1 || hydrogens.length){
                    const atomSet = [...new Set([...f.atoms,...hydrogens])];
                    let formula = getMolecularFormula(atomSet, false);

                    // PIPH-122: Add charge suffix for protonated species (e.g., H₃O⁺)
                    if (isProtonated) {
                        const totalCharge = f.atoms.reduce((sum, a) => sum + (a.charge || 0), 0);
                        if (totalCharge > 0) {
                            formula += totalCharge === 1 ? '⁺' : `${totalCharge}⁺`;
                        } else if (totalCharge < 0) {
                            formula += totalCharge === -1 ? '⁻' : `${Math.abs(totalCharge)}⁻`;
                        }
                    }
                    return formula;
                }
                return featureIdentifier.getOrganicIdentifierName(symbol);
            }
            else if(f.name.includes(Features.bridgedRing)) {
                const size = f.name.split(".")[2];
                // PIPH-119: Include bridge numbering (e.g., "2.2.1") in display name
                const bridgeNum = computeBridgeNumbering(f, graph);
                if (bridgeNum) {
                    return `${size}-membered ${bridgeNum} bicyclic bridged ring system`;
                }
                return size + FeatureDisplayNames["ring.bridge"];
            }
            else if(f.name.includes(Features.benzene)) {
                return FeatureDisplayNames[Features.benzene];
            }
            else if(f.name.includes(Features.epoxide)) {
                return FeatureDisplayNames[Features.epoxide];
            }
            else if(f.name.includes(Features.ring)) {
                const size = f.name.split(".")[1];
                return size + FeatureDisplayNames["ring"];
            }
            else if(f.name.includes(Features.methyl)) {
                return FeatureDisplayNames[Features.methyl];
            }
            else if(f.name.includes(Features.ethyl)) {
                return FeatureDisplayNames[Features.ethyl];
            }
            else if(f.name.includes(Features.chain)) {
                const size = f.name.split(".")[1];
                return size + FeatureDisplayNames["chain"];
            }
            else if(f.name.includes(Features.protonated)) {
                const featureParts = f.name.split(".");
                featureParts.pop(); // Get rid of protonated
                const baseName = featureParts.join(".")
                return FeatureDisplayNames["protonated"] + " " + FeatureDisplayNames[baseName];
            }
            else if(f.name.includes(Features.atomCluster)) {
                const size = f.name.split(".")[1];
                return size + FeatureDisplayNames["atomCluster"];
            }
            else if(f.name.includes(Features.ringSystem)) {
                const parts = f.name.split(".");
                // Handle special ring systems like naphthalene, anthracene
                if (parts[1] === "naphthalene") return FeatureDisplayNames["ringSystem.naphthalene"];
                if (parts[1] === "anthracene") return FeatureDisplayNames["ringSystem.anthracene"];
                // Generic ring system: ringSystem.{ringCount}.{atomCount}
                const ringCount = parts[1];
                const atomCount = parts[2];
                return `${ringCount}-ring ${atomCount}-atom${FeatureDisplayNames["ringSystem"]}`;
            }
            else {
                return FeatureDisplayNames[f.name];
            }
        })

        // PIPH-57: duplicate features get letter suffixes (-A, -B, ...) instead
        // of numeric ones. Letters reduce "number overload" for screen reader
        // users, who already hear many numbers from atom indices, charges, and
        // bond orders.
        //
        // PIPH-122: Consider structural context when detecting duplicates.
        // Standalone molecules (no connections) should NOT duplicate-conflict
        // with attached groups (have connections). For example, a standalone H₂O
        // molecule and an alcohol group on a chain should not both get "-A"/"-B"
        // suffixes even though they may have similar molecular formulas.
        const topLevelUniqueNames = featureIdentifier.applyAlphaIndexedNamesWithContext(
            topLevelNames,
            features.map(f => f.connections.length === 0)
        );

        features.forEach((feature, index) => {
            // set id for top level feature
            feature.uniqueName = topLevelUniqueNames[index];
            // PIPH-325: a reclassified methyl (and any functional-group
            // substituent) carries a runtime .parent but should NOT inherit an
            // " of {parent}" suffix — it is a standalone substituent name
            // ("methyl group"), exactly like bromide stays "bromide". Only
            // structural sub-features take parent context.
            if (feature.parent && classifyFeature(feature) !== "functional_group") {
                feature.uniqueName += ` of ${feature.parent.uniqueName || feature.name}`
            }
        })
    },
    /**
     * Append `-1`, `-2`, ... suffixes to any names that appear more than once
     * in the input. Names that appear only once are passed through unchanged
     * when `removeSingles` is true. Used for numeric contexts (atom position
     * enumeration) where consumers expect numeric suffixes.
     */
    applyIndexedNames: (names: string[], removeSingles: boolean = true) => {
        return featureIdentifier._applySuffixes(names, n => String(n), removeSingles);
    },
    /**
     * Like `applyIndexedNames` but uses letter suffixes (`-A`, `-B`, ..., `-Z`,
     * `-AA`, `-AB`, ...) instead of numbers. Used for feature disambiguation
     * where letters are easier for screen-reader users to distinguish from
     * atom indices and other numbers.
     */
    applyAlphaIndexedNames: (names: string[], removeSingles: boolean = true) => {
        return featureIdentifier._applySuffixes(names, toLetterSuffix, removeSingles);
    },
    /**
     * PIPH-122: Like `applyAlphaIndexedNames` but considers structural context.
     * Features are only considered duplicates if they have the same display name
     * AND the same structural type (standalone vs attached).
     *
     * This prevents false duplicates like a standalone H₂O molecule being
     * disambiguated from an alcohol group that happens to have a similar formula.
     *
     * @param names Display names for features
     * @param isStandalone Array of booleans indicating if each feature is standalone
     *                     (no connections) vs attached (has connections)
     */
    applyAlphaIndexedNamesWithContext: (names: string[], _isStandalone: boolean[]): string[] => {
        // PIPH-150: Group features by display name only, not by standalone status.
        // This ensures consistent suffixes when features with the same name appear
        // in different structural contexts. If only one feature has a given name,
        // it gets no suffix regardless of whether it's standalone or attached.
        const signatureGroups = new Map<string, number[]>();

        names.forEach((name, i) => {
            const key = name;
            const group = signatureGroups.get(key) || [];
            group.push(i);
            signatureGroups.set(key, group);
        });

        // Build result array
        const result: string[] = new Array(names.length);

        for (const [_, indices] of signatureGroups) {
            if (indices.length === 1) {
                // Unique within structural context - no suffix needed
                result[indices[0]] = names[indices[0]];
            } else {
                // Multiple features with same name and structural type - add suffixes
                indices.forEach((originalIndex, groupIndex) => {
                    result[originalIndex] = `${names[originalIndex]}-${toLetterSuffix(groupIndex + 1)}`;
                });
            }
        }

        return result;
    },
    /** @internal shared loop for applyIndexedNames / applyAlphaIndexedNames */
    _applySuffixes: (
        names: string[],
        formatSuffix: (n: number) => string,
        removeSingles: boolean,
    ): string[] => {
        const nameIds: Record<string, number> = {};
        const indexedNames: string[] = [];

        names.forEach(name => {
            if (removeSingles && names.indexOf(name) === names.lastIndexOf(name)) {
                // no duplicate so don't add suffix
                indexedNames.push(name);
            } else {
                nameIds[name] = (nameIds[name] ?? 0) + 1;
                indexedNames.push(`${name}-${formatSuffix(nameIds[name])}`);
            }
        });

        return indexedNames;
    },
    applyRingIndexedNames: (atoms: GraphAtom[]) => {
        const indexedNames: string[] = [];

        atoms.forEach((a,i) => {
            const element = featureIdentifier.getOrganicIdentifierName(a.symbol);
            indexedNames.push(`${element}-${i+1}`);
        })

        return indexedNames;
    }
}
